import React, { useEffect, useState } from 'react'
import { Copy, InfoTooltip, Link } from '../../../components'
import { WemoloTypography } from '../../../components/Wemolo'
import { getBalenaStatusIndicator, useBalenaDeviceData, useBalenaRelease } from '../../../utils'
import { useGetDeviceData } from '../../StationView/utils/station-hooks'
import { getValueFromConfig } from '../../../utils/getValueFromConfig'
import { DeviceDataType } from '../../DeviceConfig'
import { getDeviceStatus, getStatusColor } from '../../DeviceConfig/utils/getDeviceStatus'
import {
    getBalenaUrl,
    getCameraUrl,
    getSemver,
    getIsRunningReleaseId,
    getShouldBeRunningReleaseId,
    getTelktonikaRmsUrl
} from '../../DeviceConfig/utils'

import { DateTime } from 'luxon'
import { LotTableActions } from '../LotTableActions'
import { LotStationType } from '../lot-types'
import { DeviceTypeEnum } from '../../../data/enums'
import { LotOccupancyFreeSpaces } from '../../Occupancy/LotOccupancyFreeSpaces'
import { LotOccupancyTrafficLightColor } from '../../Occupancy/LotOccupancyTrafficLightColor'
import styles from './lotUtils.module.css'

// Mirrors the Pipeline Mode options in the device config form (DeviceBasicConfig*.json), so the
// table reads the same as the form that sets it. Anything unrecognised keeps its raw name with the
// underscores opened up, which at least stays readable and can wrap.
const PIPELINE_MODE_LABEL: Record<string, string> = {
    normal: 'Normal',
    multilot_static: 'Multi Lot',
    carpay_display: 'Car-Pay Display'
}

const getPipelineModeLabel = (mode: string | boolean | null): string => {
    if (typeof mode !== 'string' || mode === '-' || !mode) {
        return '-'
    }
    return PIPELINE_MODE_LABEL[mode] ?? mode.replace(/_/g, ' ')
}

export const colorOnAndOff = (value: string) => {
    if (value.toLowerCase() === 'on') {
        return (
            <WemoloTypography variant="body-medium" tag="span" className="text-green">
                {value}
            </WemoloTypography>
        )
    } else if (value.toLowerCase() === 'off') {
        return (
            <WemoloTypography variant="body-medium" tag="span" className="text-red">
                {value}
            </WemoloTypography>
        )
    }
    return value
}
export const stationEnabledCell = (enabled: boolean) => (
    <WemoloTypography
        variant="body-small"
        tag="span"
        className={enabled ? styles.stationEnabled : styles.stationDisabled}
    >
        {enabled ? 'Enabled' : 'Disabled'}
    </WemoloTypography>
)

export const LotDeviceBalenaStatus = ({ deviceUUID }: { deviceUUID: string | undefined }) => {
    const { balenaDeviceData, loading } = useBalenaDeviceData(deviceUUID)
    if (loading) {
        return <span>Loading...</span>
    }
    const deviceBalenaRawData = balenaDeviceData?.devices?.[0]
    const getToolTipItem = (title: string, value: any, copy = true) => (
        <div className={styles.tooltipItem}>
            <b> {title}</b> : &nbsp; {/* {deviceContext.runningCommit || '-'}{' '} */}
            {copy ? <Copy color="white" item={value} value={value} /> : value}
        </div>
    )
    return (
        <span>
            <InfoTooltip
                content={
                    <>
                        {getToolTipItem('VPN Connected', deviceBalenaRawData?.is_connected_to_vpn + '', false)}
                        {getToolTipItem('API Heartbeat State', deviceBalenaRawData?.api_heartbeat_state + '', false)}
                        {getToolTipItem(
                            'Last connectivity event',
                            DateTime.fromISO(deviceBalenaRawData?.last_connectivity_event ?? '').toRelative({
                                locale: 'en'
                            }),
                            false
                        )}
                        {getToolTipItem(
                            'Last VPN event',
                            DateTime.fromISO(deviceBalenaRawData?.last_vpn_event ?? '').toRelative({
                                locale: 'en'
                            }),
                            false
                        )}
                    </>
                }
            >
                {getBalenaStatusIndicator(deviceBalenaRawData)}
            </InfoTooltip>
            {getBalenaStatusIndicator(deviceBalenaRawData)?.props?.children === 'OFFLINE' && (
                <div className={styles.balenaOfflineDate}>
                    {DateTime.fromISO(deviceBalenaRawData?.last_connectivity_event ?? '').toRelative({
                        locale: 'en'
                    })}
                    &nbsp;
                </div>
            )}
        </span>
    )
}

export const LotDeviceConfigStatus = ({ deviceUUID }: { deviceUUID: string | undefined }) => {
    const { balenaDeviceData, loading } = useBalenaDeviceData(deviceUUID)
    const { balenaReleaseData: balenaRunningReleaseData, refetchBalenaRelease: refetchBalenaRunningRelease } =
        useBalenaRelease(getIsRunningReleaseId(balenaDeviceData))
    const { balenaReleaseData: balenaTargetRealeaseData, refetchBalenaRelease: refetchBalenaTargetRelease } =
        useBalenaRelease(getShouldBeRunningReleaseId(balenaDeviceData))
    const { data: deviceData } = useGetDeviceData(deviceUUID)
    useEffect(() => {
        if (getIsRunningReleaseId(balenaDeviceData)) {
            refetchBalenaRunningRelease()
        }
        if (getShouldBeRunningReleaseId(balenaDeviceData)) {
            refetchBalenaTargetRelease()
        }
    }, [balenaDeviceData, refetchBalenaRunningRelease, refetchBalenaTargetRelease])

    if (loading) {
        return <span>Loading...</span>
    }

    if (!deviceData) {
        return <>-</>
    }

    const targetCommit = getSemver(balenaTargetRealeaseData)
    const runningCommit = getSemver(balenaRunningReleaseData)
    const getToolTipItem = (title: string, value: any, copy = true) => (
        <div className={styles.tooltipItem}>
            <b> {title}</b> : &nbsp;
            {copy ? <Copy color="white" item={value} value={value} /> : value}
        </div>
    )

    return (
        <InfoTooltip
            content={
                <>
                    {getToolTipItem('Release Target', targetCommit, false)}
                    {getToolTipItem('Release Currently', runningCommit, false)}
                </>
            }
        >
            {getStatusColor(getDeviceStatus(deviceData.device[0]))}
        </InfoTooltip>
    )
}
export const getRmsDeviceName = (lotStation: LotStationType): string | null => {
    const deviceName = lotStation.device?.device_name
    if (
        lotStation.type === DeviceTypeEnum.SCANNER ||
        lotStation.type === DeviceTypeEnum.TERMINAL ||
        lotStation.type === DeviceTypeEnum.DISPLAY
    ) {
        return deviceName || null
    }
    return null
}

const ExtraCell = ({ lotStation }: { lotStation: LotStationType }) => {
    if (lotStation.type === DeviceTypeEnum.TRAFFIC_LIGHT) {
        return <LotOccupancyTrafficLightColor />
    }
    if (lotStation.type === DeviceTypeEnum.OCCUPANCY_DISPLAY) {
        return <LotOccupancyFreeSpaces lotStation={lotStation} />
    }

    return null
}
export const getLotDevicesValues = (lotStation: LotStationType, onEditModalClick: any) => {
    const installationMode = getValueFromConfig(lotStation.device?.config?.base, lotStation.device?.config?.diff, [
        'vehicle_detection',
        'camera-settings',
        'cameras',
        '[0]',
        'enable_installation_mode'
    ])
    const pipelineMode = getValueFromConfig(lotStation.device?.config?.base, lotStation.device?.config?.diff, [
        'vehicle_detection',
        'pipeline_mode'
    ])
    const zoom = getValueFromConfig(lotStation.device?.config?.base, lotStation.device?.config?.diff, [
        'vehicle_detection',
        'camera-settings',
        'cameras',
        '[0]',
        'zoom_value'
    ])
    const focus = getValueFromConfig(lotStation.device?.config?.base, lotStation.device?.config?.diff, [
        'vehicle_detection',
        'camera-settings',
        'cameras',
        '[0]',
        'focus_value'
    ])
    const isZoomAndFocusEnabled = getValueFromConfig(lotStation.device?.config?.base, lotStation.device?.config?.diff, [
        'vehicle_detection',
        'camera-settings',
        'cameras',
        '[0]',
        'enable_set_zoom_focus'
    ])
    const tableObject = {
        deviceId: lotStation.device_uuid ? (
            <span className={styles.deviceIdCell}>
                <span className={styles.deviceIdLines}>
                    <span className={styles.deviceIdLine}>
                        <Link
                            link={`/device?deviceId=${lotStation?.device?.uuid}`}
                            target="_self"
                            text={lotStation.device?.device_name || '-'}
                        />
                    </span>
                    <span className={styles.deviceIdLine}>
                        <Link
                            link={`/device?deviceId=${lotStation?.device?.uuid}`}
                            target="_self"
                            text={lotStation.device_uuid}
                        />
                    </span>
                </span>
                <Copy item={lotStation.device_uuid} showText={false} />
            </span>
        ) : (
            '-'
        ),
        stationName: lotStation.station_name ? (
            <Link
                link={`/station?stationName=${lotStation.station_name}`}
                target="_self"
                text={lotStation.station_name}
            />
        ) : (
            '-'
        ),
        enabled: stationEnabledCell(lotStation.enabled),
        onlineStatus: lotStation.device?.uuid ? <LotDeviceBalenaStatus deviceUUID={lotStation.device?.uuid} /> : '-',
        deviceConfigStatus: lotStation.device ? <LotDeviceConfigStatus deviceUUID={lotStation.device?.uuid} /> : '-',
        direction: lotStation.device_direction || '-',
        autoWhitelist: colorOnAndOff((lotStation?.config?.autowhitelist?.enable && 'ON') || 'Off'),
        installationMode: colorOnAndOff(
            Boolean(lotStation.device?.uuid)
                ? installationMode
                    ? installationMode !== '-'
                        ? 'On'
                        : 'Off'
                    : 'Off'
                : '-'
        ),
        pipelineMode: getPipelineModeLabel(pipelineMode),
        zoomAndFocus: isZoomAndFocusEnabled && isZoomAndFocusEnabled !== '-' ? `${zoom} / ${focus}` : 'Disabled',
        links: lotStation.device?.uuid ? (
            <div>
                {lotStation?.device_uuid?.includes('balena_') && (
                    <Link text={'Balena'} link={getBalenaUrl((lotStation?.device_uuid + '').replace('balena_', ''))} />
                )}
                {lotStation.type === DeviceTypeEnum.SCANNER && (
                    <Link
                        text={'Live View (VPN)'}
                        link={getCameraUrl(lotStation.device as DeviceDataType) || undefined}
                    ></Link>
                )}
                {getRmsDeviceName(lotStation) && (
                    <Link text={'RMS'} link={getTelktonikaRmsUrl(getRmsDeviceName(lotStation)) || ''} />
                )}
            </div>
        ) : (
            '-'
        ),
        actions: <LotTableActions onEditModalClick={onEditModalClick} canLink={!Boolean(lotStation.device?.uuid)} />,
        extra: <ExtraCell lotStation={lotStation} />
    }
    return tableObject
}

// Widths are sized to the widest value a column actually holds, so nothing is clipped and the table
// scrolls horizontally as one instead of squeezing every cell.
export const lotTableColumns = [
    { key: 'stationName', label: 'Station Name', width: '120px' },
    { key: 'enabled', label: 'Enabled', width: '80px' },
    { key: 'deviceId', label: 'Device ID', width: '130px' },
    { key: 'onlineStatus', label: 'Online Status', width: '120px' },
    // Narrow enough that the label breaks after each word instead of running two words wide.
    { key: 'deviceConfigStatus', label: 'Device Configuration Status', width: '130px' },
    { key: 'direction', label: 'Direction', width: '80px' },
    { key: 'autoWhitelist', label: 'Auto Whitelist', width: '90px' },
    { key: 'installationMode', label: 'Installation Mode', width: '100px' },
    { key: 'pipelineMode', label: 'Pipeline Mode', width: '110px' },
    { key: 'zoomAndFocus', label: 'Zoom / Focus', width: '110px' },
    { key: 'links', label: 'Links', width: '110px' },
    { key: 'actions', label: 'Actions', width: '70px' }
]

export const carpayDeviceTableColumns = [
    { key: 'stationName', label: 'Station Name', width: '120px' },
    { key: 'deviceId', label: 'Device ID', width: '130px' },
    { key: 'onlineStatus', label: 'Online Status', width: '120px' },
    { key: 'deviceConfigStatus', label: 'Device Configuration Status', width: '130px' },
    { key: 'links', label: 'Links', width: '110px' },
    // Wide enough for the occupancy plate plus its timestamp and refresh on one line.
    { key: 'extra', label: 'Extra', width: '170px' }
]
